import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, WritableSignal, inject, signal, untracked} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {FormCheckboxComponent} from "../../../shared/elements/form-checkbox/form-checkbox.component";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {FormTextareaComponent} from "../../../shared/elements/form-textarea/form-textarea.component";
import {FormUserGroupMailComponent} from "../../../shared/elements/form-user-group-mail/form-user-group-mail.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {NgOptimizedImage} from "@angular/common";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {TYPE} from "../../../shared/types/types";
import {DialogService} from "../../../shared/services/dialog.service";
import {StepHeaderComponent} from "../../../shared/elements/step-header/step-header.component";
import {LanguageService} from "../../../shared/services/language.service";
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {StepContentComponent} from "../../../shared/elements/step-content/step-content.component";
import {Subscription, take} from "rxjs";
import {DocumentService} from "../../../shared/services/document.service";
import {ApiService} from "../../../shared/services/api.service";
import {ErrorService} from "../../../shared/services/error.service";
import {LayoutService} from '../../../shared/services/layout.service';
import {Router} from '@angular/router';
import {DocumentSelectorComponent} from "../../documents/document-selector/document-selector.component";
import {FormDateTimeComponent} from "../../../shared/elements/form-date-time/form-date-time.component";
import {FormRadioComponent} from "../../../shared/elements/form-radio/form-radio.component";
import {KnowledgebaseContentResource} from '../../../shared/types/knowledgebase-types/knowledgebaseContentResource';
import {convertFilesToDocuments} from '../../../shared/mapper/file-interop';

@Component({
    selector: 'app-dialog-share',
    imports: [
        DialogModule,
        FormButtonComponent,
        FormCheckboxComponent,
        FormInputComponent,
        FormLabelComponent,
        FormTextareaComponent,
        FormUserGroupMailComponent,
        HeadlineComponent,
        IconBadgeComponent,
        LinkComponent,
        TooltipComponent,
        TooltipModule,
        TranslatePipe,
        StepHeaderComponent,
        StepContentComponent,
        NgOptimizedImage,
        DocumentSelectorComponent,
        FormDateTimeComponent,
        FormRadioComponent
    ],
    templateUrl: './dialog-share.component.html',
    styleUrl: './dialog-share.component.scss'
})
export class DialogShareComponent implements OnInit, OnDestroy {
  //#region injections
  public dialogService: DialogService = inject(DialogService);
  public layoutService: LayoutService = inject(LayoutService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private languageService: LanguageService = inject(LanguageService);
  private documentService: DocumentService = inject(DocumentService);
  private apiService: ApiService = inject(ApiService);
  private errorService: ErrorService = inject(ErrorService);
  private router: Router = inject(Router);
  //#endregion injections

  //#region IO
  @Input() public data: (TYPE.NewsListItem | KnowledgebaseContentResource) | null = null;
  @Input() public communityDetailEntry: TYPE.CommunityDetailEntry | null = null;
  @Input() public visible: boolean = false;
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  //#endregion IO

  //#region dialog control attributes
  private subscriptions: Subscription[] = [];

  public steps: string[] = [
    this.languageService.getTranslationByKey('SHAREUSERSGROUPSMENUTEXT'),
    this.languageService.getTranslationByKey('SHARECHATMENUTEXT'),
    this.languageService.getTranslationByKey('SHAREMAILMENUTEXT'),
    // TODO: leave the share to tv option out for now
    // this.languageService.getTranslationByKey('SHARETVMENUTEXT')
  ];
  public stepIcons: string[] = ['group', 'chat', 'mail', 'link', 'public', 'tv'];
  public activeStep: number = 0;
  //#endregion dialog control attributes

  //#region form definitions
  public shareNewsForm: FormGroup = this.formBuilder.group({
    title: ['', [Validators.required, Validators.minLength(1)]],
    message: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1000)]],
    files: [[]],
    startDate: [null],
    endDate: [null],
    allowComments: [true],
    allowSharing: [false],
    groups: [[]],
    users: [[]],
    participantsUserIDs: [[]],
    participantsGroupIDs: [[]],
    pushUserIDs: [[]],
    pushGroupIDs: [[]],
  });

  public shareChatForm: FormGroup = this.formBuilder.group({
    roomSearch: [''],
    selectedRooms: new FormArray([], [this.atLeastOneSelected()]),
  });

  public shareMailForm: FormGroup = this.formBuilder.group({
    recipientEmail: ['', [Validators.required, Validators.email]],
    subject: ['', [Validators.required, Validators.minLength(1)]],
    message: ['', [Validators.required, Validators.minLength(1)]],
    files: [[]],
  });

  public shareTvForm: FormGroup = this.formBuilder.group({
    title: ['', [Validators.required, Validators.minLength(1)]],
    internalNote: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(30)]],
    message: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1000)]],
    files: [[], [Validators.maxLength(1)]],
  });
  //#endregion form definitions

  //#region chat form attributes
  private previousChatRoomString: WritableSignal<string | null> = signal(null);
  private chatRoomSearchPage: WritableSignal<number> = signal(0);
  private chatRoomSearchMaxReached: WritableSignal<boolean> = signal(false);
  public selectableChatRooms: WritableSignal<TYPE.ChatListItem[]> = signal<TYPE.ChatListItem[]>([]);
  //#endregion chat form attributes

  //#region news form attributes
  public selectableNewsFiles: WritableSignal<TYPE.Document[]> = signal<TYPE.Document[]>([]);
  //#endregion news form attributes

  //#region mail form attributes
  public selectableMailFiles: WritableSignal<TYPE.Document[]> = signal<TYPE.Document[]>([]);
  //#endregion mail form attributes
  
  //#region tv form attributes
  public selectableTvFiles: WritableSignal<TYPE.Document[]> = signal<TYPE.Document[]>([]);
  //#endregion tv form attributes

  //#region dialog functions
  public ngOnInit(): void {
    this.fillNewsFormValues();
    this.fillChatFormValues();
    this.fillMailFormValues();
    this.fillTvFormValues();

    this.subscriptions.push(
      this.layoutService.scrolledToEnd.subscribe({
        next: (scrolledToEnd: boolean | string) => {
          if (scrolledToEnd && scrolledToEnd === 'share-page-1') {
            untracked(() => {
              this.filterResults();
            });
          }
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public setStep(index: number): void {// fake
    this.activeStep = index;
  }
  //#endregion dialog functions

  //#region submit functions
  public createNews(): void {
    // const participantsUserIDs: number[] = this.shareNewsForm.controls['participantsUserIDs'].value;
    // const participantsGroupIDs: number[] = this.shareNewsForm.controls['participantsGroupIDs'].value;
    // const pushUserIDs: number[] = this.shareNewsForm.controls['pushUserIDs'].value;
    // const pushGroupIDs: number[] = this.shareNewsForm.controls['pushGroupIDs'].value;

    // // if it has users as recipients, tell the api by sending group id -2
    // if (participantsUserIDs && participantsUserIDs.length > 0) {
    //   participantsGroupIDs.push(-2);
    // }

    // const users: string = participantsUserIDs.join(',');
    // const usersPush: string = pushUserIDs.join(',');
    // const groups: string = participantsGroupIDs.join(',');
    // const groupsPush: string = pushGroupIDs.join(',');

    // this.data!.id = 0;
    // TODO: share dialog - share to news
    console.log('TODO: implement share to news');
    // this.subscriptions.push(
    //   this.apiService.setNews(
    //     (this.data as TYPE.NewsListItem)!,
    //     groups, 
    //     groupsPush, 
    //     users, 
    //     usersPush, 
    //     this.documentService.mergeDocuments((this.data as TYPE.NewsListItem)!.images, (this.data as TYPE.NewsListItem)!.videos, (this.data as TYPE.NewsListItem)!.files
    //     )).subscribe({
    //       next: () => {
    //         this.close();
    //       }, error: (error) => {
    //         this.errorService.setError(this.languageService.getTranslationByKey('ERROR'), error, true, false);
    //       }
    //   })
    // );
  }

  public shareByMail(): void {

    this.apiService.shareByMail({
      recipient: this.shareMailForm.controls['recipientEmail'].value,
      title: this.shareMailForm.controls['subject'].value,
      message: this.shareMailForm.controls['message'].value,
      files: this.shareMailForm.controls['files'].value,
      return_link: false
    })
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.close();
      }, error: (error) => {
        this.errorService.setError(this.languageService.getTranslationByKey('MAILSHAREERROR'), error, true, false);
      }
    });
  }

  public shareToChat(share: boolean): void {
    // const files: TYPE.Document[] = [];
    // const message: string = '';
    // handle CatecoryContent
    if(Object.prototype.hasOwnProperty.call(this.data, 'content')) {
      // const data: KnowledgebaseContentResource = this.data as KnowledgebaseContentResource;
      // files = convertFilesToDocuments(data.files) ?? [];
      // message = (data.title ?? '') + '\n' + (data.content ?? '');
    } else {  // handle NewsListItem
      // const data: TYPE.NewsListItem = this.data as TYPE.NewsListItem;
      // files = data.files ?? [];
      // message = (data.title ?? '') + '\n' + (data.message_web ?? '');
    }

    const selectedRooms: TYPE.ChatListItem[] = [];
    ((this.shareChatForm.controls['selectedRooms'] as FormArray).controls ?? [])
    .forEach((control, index) => {
      if(control.value === true) {
        selectedRooms.push(this.selectableChatRooms()[index]);
      }
    });
    // selectedRooms.forEach((room: TYPE.ChatListItem) => {
    //   files.forEach((file: TYPE.Document) => {
    //     this.apiService.addChatMessage(
    //       room.id,
    //       '',
    //       [file],
    //       [],
    //       [],
    //       true
    //     ).pipe(take(1)).subscribe();
    //   });
    //   this.apiService.addChatMessage(
    //     room.id,
    //     message,
    //     [],
    //     [],
    //     [],
    //     true
    //   ).pipe(take(1)).subscribe();
    // });

    if(share) {
      this.router.navigate([`/chat/detail/0/0`]);
    } else {
      this.close();
    }
  }

  public shareTvPreview(): void {
    const data = {
      title: this.shareTvForm.controls['title'].value,
      description: this.shareTvForm.controls['message'].value,
      files: this.shareTvForm.controls['files'].value
    };
    localStorage.setItem("contentPreviewData", JSON.stringify(data));
    // const link = baseURL + '/tv-admin/content-preview';
    // window.open(link, '_blank');
    window.alert('TODO: implement TV preview link opening');
  }

  public shareTv(): void {
    window.alert('TODO: implement TV share');
  };
  //#endregion submit functions

  //#region fill form values
    private fillNewsFormValues(): void {
    // handle CatecoryContent
    if(Object.prototype.hasOwnProperty.call(this.data, 'content')) {
      const data: KnowledgebaseContentResource = this.data as KnowledgebaseContentResource;
      this.shareNewsForm.controls['title'].setValue(data.title ?? '');
      this.shareNewsForm.controls['message'].setValue(data.content ?? '');
      this.selectableNewsFiles.set(convertFilesToDocuments(data.files) ?? []);
      this.shareNewsForm.controls['files'].setValue([]);
      this.shareNewsForm.controls['title'].markAsDirty();
      this.shareNewsForm.controls['message'].markAsDirty();
    } else {  // handle NewsListItem
      const data: TYPE.NewsListItem = this.data as TYPE.NewsListItem;
      this.shareNewsForm.controls['title'].setValue(data.title ?? '');
      this.shareNewsForm.controls['message'].setValue(data.message_web ?? '');
      this.selectableNewsFiles.set([...data.files, ...data.images, ...data.videos]);
      this.shareNewsForm.controls['files'].setValue([]);
      this.shareNewsForm.controls['title'].markAsDirty();
      this.shareNewsForm.controls['message'].markAsDirty();
    }
  }

  private fillChatFormValues(): void {
    this.filterResults();
  }

  private fillMailFormValues(): void {
    // handle CatecoryContent
    if(Object.prototype.hasOwnProperty.call(this.data, 'content')) {
      const data: KnowledgebaseContentResource = this.data as KnowledgebaseContentResource;
      this.shareMailForm.controls['subject'].setValue(data.title ?? '');
      this.shareMailForm.controls['message'].setValue(data.content ?? '');
      this.selectableMailFiles.set(convertFilesToDocuments(data.files) ?? []);
      this.shareMailForm.controls['files'].setValue([]);
    } else {  // handle NewsListItem
      const data: TYPE.NewsListItem = this.data as TYPE.NewsListItem;
      this.shareMailForm.controls['subject'].setValue(data.title ?? '');
      this.shareMailForm.controls['message'].setValue(data.message_web ?? '');
      this.selectableMailFiles.set([...data.files, ...data.images, ...data.videos]);
      this.shareMailForm.controls['files'].setValue([]);
    }
  }

  private fillTvFormValues(): void {
    // handle CatecoryContent
    if(Object.prototype.hasOwnProperty.call(this.data, 'content')) {
      const data: KnowledgebaseContentResource = this.data as KnowledgebaseContentResource;
      this.shareTvForm.controls['title'].setValue(data.title ?? '');
      this.shareTvForm.controls['message'].setValue(data.content ?? '');
      this.selectableTvFiles.set(convertFilesToDocuments(data.files) ?? []);
      this.shareTvForm.controls['files'].setValue([]);
    } else {  // handle NewsListItem
      const data: TYPE.NewsListItem = this.data as TYPE.NewsListItem;
      this.shareTvForm.controls['title'].setValue(data.title ?? '');
      this.shareTvForm.controls['message'].setValue(data.message_web ?? '');
      this.selectableTvFiles.set([...data.files, ...data.images, ...data.videos]);
      this.shareTvForm.controls['files'].setValue([]);
    }
  }
  //#endregion fill form values

  //#region formHelpers
    //#region chat form helpers
    public filterResults(): void {
      // if(this.shareChatForm.controls['roomSearch'].value !== this.previousChatRoomString()) {
      //   this.previousChatRoomString.set(this.shareChatForm.controls['roomSearch'].value);
      //   this.chatRoomSearchPage.set(0);
      //   this.chatRoomSearchMaxReached.set(false);
      //   this.selectableChatRooms.set([]);
      //   this.shareChatForm.controls['selectedRooms'].setValue([]);
      // }
      // if(!this.chatRoomSearchMaxReached()) {
      //   this.apiService.getChats(
      //     this.chatRoomSearchPage(),
      //     this.previousChatRoomString() ?? '',
      //     'all')
      //     .pipe(take(1))
      //     .subscribe({
      //       next: (rooms: TYPE.ChatListItem[]) => {
      //         if(rooms.length === 0) {
      //           this.chatRoomSearchMaxReached.set(true);
      //         }
      //         this.selectableChatRooms.set([...this.selectableChatRooms(), ...rooms]);
      //         this.chatRoomSearchPage.set(this.chatRoomSearchPage() + 1);
      //         rooms.forEach(() => {
      //           (this.shareChatForm.controls['selectedRooms'] as FormArray).push(new FormControl(false));
      //         });
      //       },
      //       error: (error) => {
      //         this.errorService.setError(this.languageService.getTranslationByKey('ERROR'), error, true, false);
      //       }
      //     });
      // }
    }
    
    public getFormArray(): FormGroup {
      return (this.shareChatForm.controls['selectedRooms'] as FormGroup)
    }

    public selectAllRooms(): void {
      (this.shareChatForm.controls['selectedRooms'] as FormArray).controls
      .forEach((control) => {
        control.setValue(true);
      });
    }

    public removeAllSelectedRooms(): void {
      (this.shareChatForm.controls['selectedRooms'] as FormArray).controls
      .forEach((control) => {
        control.setValue(false);
      });
    }

    private atLeastOneSelected(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
        const formArray = control as FormArray;
        const selected = formArray.controls.map(ctrl => ctrl.value).find(value => value === true);
        return selected ? null : {'atLeastOne': false};
      };
    }

    //#endregion chat form helpers
  //#endregion formHelpers
}
